import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';

import * as customPropTypes from '@/custom-prop-types';
import Typography from '@/design-system/atoms/Typography';

import * as Card from '../components';

/**
 * Search Results Card - Generic
 * @param {object} props
 * @param {object} props.badge - Badge object
 * @param {string} props.className - Additional class name
 * @param {string} props.description - Description
 * @param {object} props.link - Link object
 * @param {string} props.title - Card title
 * @param {object} props.analytics - Analytics object
 * @param {boolean} props.isInternal - Is internal link
 * @returns
 */
const SearchResultsCardGeneric = React.forwardRef(
    (
        {
            analytics,
            badge,
            className,
            description,
            isInternal,
            link,
            title,
            coveoAnalyticsCallbacks,
            ...props
        },
        ref,
    ) => {
        const handleClick = useCallback(() => {
            coveoAnalyticsCallbacks?.onClick?.();
        }, [coveoAnalyticsCallbacks]);

        const handleContextMenu = useCallback(() => {
            coveoAnalyticsCallbacks?.onContextMenu?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseDown = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseDown?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseUp = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseUp?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchStart = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchStart?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchEnd = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchEnd?.();
        }, [coveoAnalyticsCallbacks]);

        useEffect(() => {
            coveoAnalyticsCallbacks?.cancelPendingSelect?.();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <Card.Container className={className} ref={ref} {...props}>
                <Card.Header>
                    {badge?.type && badge?.label && (
                        <Card.Badge type={badge.type} label={badge.label} />
                    )}
                </Card.Header>
                <Card.Content>
                    <Card.Title
                        analytics={
                            analytics ? { ...analytics, componentTitle: badge?.label } : undefined
                        }
                        isInternal={isInternal}
                        link={link}
                        title={title}
                        onClick={coveoAnalyticsCallbacks ? handleClick : undefined}
                        onContextMenu={coveoAnalyticsCallbacks ? handleContextMenu : undefined}
                        onMouseDown={coveoAnalyticsCallbacks ? handleMouseDown : undefined}
                        onMouseUp={coveoAnalyticsCallbacks ? handleMouseUp : undefined}
                        onTouchEnd={coveoAnalyticsCallbacks ? handleTouchEnd : undefined}
                        onTouchStart={coveoAnalyticsCallbacks ? handleTouchStart : undefined}
                    />
                    <Typography variant="t2" content={description} lineClamp={{ sm: 4, lg: 2 }} />
                </Card.Content>
            </Card.Container>
        );
    },
);

SearchResultsCardGeneric.propTypes = {
    badge: PropTypes.shape({
        type: PropTypes.string,
        label: PropTypes.string,
    }),
    className: PropTypes.string,
    description: PropTypes.string,
    isInternal: PropTypes.bool,
    link: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    title: PropTypes.string,
    analytics: customPropTypes.analyticsPropType,
};

SearchResultsCardGeneric.displayName = 'SearchResultsCardGeneric';

export default SearchResultsCardGeneric;
